import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { newsCategories } from '@/mocks/newsData';
import { supabase } from '@/lib/supabase';
import { motion } from 'motion/react';

interface ArticleFromDB {
  id: string;
  title: string;
  slug: string;
  category: string;
  excerpt: string | null;
  image_url: string | null;
  author: string | null;
  read_time: string | null;
  featured: boolean;
  created_at: string;
}

interface ArticleUI {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categoryName: string;
  image: string;
  author: string;
  readTime: string;
  featured: boolean;
  date: string;
}

const catLabels: Record<string, string> = {
  'company-news': 'Company News',
  'industry-insight': 'Industry Insight',
  'partnerships': 'Partnerships',
  'trade-updates': 'Trade Updates',
  'case-studies': 'Case Studies',
};

function toUI(article: ArticleFromDB): ArticleUI {
  return {
    id: article.slug,
    slug: article.slug,
    title: article.title,
    excerpt: article.excerpt || '',
    category: article.category,
    categoryName: catLabels[article.category] || article.category,
    image: article.image_url || 'https://readdy.ai/api/search-image?query=Modern%20corporate%20business%20news%20and%20insights%20abstract%20professional%20setting%20clean%20composition%20warm%20lighting&width=800&height=600&seq=article-placeholder&orientation=landscape',
    author: article.author || 'BEKAALI Communications',
    readTime: article.read_time || '5 min read',
    featured: article.featured,
    date: article.created_at,
  };
}

export default function News() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(6);

  const [allArticles, setAllArticles] = useState<ArticleUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchErr } = await supabase
        .from('articles')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      setAllArticles((data || []).map(toUI));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const filteredArticles = useMemo(() => {
    let result = allArticles;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.categoryName.toLowerCase().includes(q)
      );
    } else if (activeCategory !== 'all') {
      result = result.filter((a) => a.category === activeCategory);
    }
    return result;
  }, [search, activeCategory, allArticles]);

  const visibleArticles = filteredArticles.slice(0, visibleCount);
  const hasMore = visibleCount < filteredArticles.length;

  const featuredArticle = !search && activeCategory === 'all'
    ? allArticles.find((a) => a.featured) || null
    : null;

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">
        {/* Header */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20 bg-background-100">
          <motion.span initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Stay Informed</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }} className="text-4xl md:text-6xl font-heading font-bold text-foreground-950 mb-4">News &amp; Insights</motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.2 }} className="text-foreground-600 max-w-2xl text-sm md:text-base leading-relaxed">
            Latest company updates, industry analysis, trade developments, and success stories from BEKAALI ENTERPRISES LIMITED.
          </motion.p>
        </div>

        <div className="w-full px-6 md:px-10 lg:px-16">
          {/* Search + Category Filters */}
          <div className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    if (e.target.value) setActiveCategory('all');
                    setVisibleCount(6);
                  }}
                  placeholder="Search articles by keyword..."
                  className="w-full pl-11 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-full focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {newsCategories.map((cat) => {
                  const count = cat.id === 'all' ? allArticles.length : allArticles.filter((a) => a.category === cat.id).length;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveCategory(cat.id);
                        setSearch('');
                        setVisibleCount(6);
                      }}
                      className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeCategory === cat.id && !search
                          ? 'bg-accent-500 text-primary-900'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${cat.icon} text-xs`}></i>
                      {cat.name}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        activeCategory === cat.id && !search ? 'bg-primary-900/15' : 'bg-background-200'
                      }`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {!search && !loading && (
              <p className="text-sm text-foreground-500 mt-3">
                Showing <strong className="text-foreground-800">{visibleArticles.length}</strong> of <strong className="text-foreground-800">{filteredArticles.length}</strong> articles
                {activeCategory !== 'all' && <> in <strong className="text-foreground-800">{newsCategories.find((c) => c.id === activeCategory)?.name}</strong></>}
              </p>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-24">
              <div className="w-10 h-10 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50 text-red-400">
                <i className="ri-error-warning-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-2">Failed to load articles</h3>
              <p className="text-sm text-foreground-500 mb-4">{error}</p>
              <button onClick={fetchArticles} className="px-5 py-2.5 text-sm font-medium bg-accent-500 text-primary-900 rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Try Again
              </button>
            </div>
          )}

          {/* Content */}
          {!loading && !error && (
            <>
              {/* Featured Article */}
              {featuredArticle && (
                <div className="pb-10">
                  <Link
                    to={`/news/${featuredArticle.slug}`}
                    className="group relative bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400 cursor-pointer flex flex-col lg:flex-row"
                  >
                    <div className="lg:w-1/2 relative overflow-hidden aspect-[16/10] lg:aspect-auto">
                      <img
                        src={featuredArticle.image}
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-primary-950/20"></div>
                      <span className="absolute top-4 left-4 px-2.5 py-1 bg-accent-500 text-primary-900 text-[10px] font-semibold uppercase tracking-wider rounded-full">Featured</span>
                    </div>
                    <div className="lg:w-1/2 p-6 md:p-8 lg:p-10 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full">{featuredArticle.categoryName}</span>
                        <span className="text-xs text-foreground-400">{featuredArticle.readTime}</span>
                      </div>
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 leading-snug group-hover:text-primary-600 transition-colors duration-200">{featuredArticle.title}</h2>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-3">{featuredArticle.excerpt}</p>
                      <div className="flex items-center gap-2">
                        <time className="text-xs text-foreground-400">{new Date(featuredArticle.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
                        <span className="text-foreground-300">·</span>
                        <span className="text-xs text-foreground-400">{featuredArticle.author}</span>
                      </div>
                      <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-accent-600 group-hover:text-accent-500 transition-colors whitespace-nowrap">
                        Read Article <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </Link>
                </div>
              )}

              {/* Article Grid */}
              <div className="pb-10">
                {filteredArticles.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                      <i className="ri-article-line text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-2">No articles found</h3>
                    <p className="text-sm text-foreground-500">Try adjusting your search or browse a different category.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                      {visibleArticles.map((article, idx) => {
                        if (featuredArticle && article.id === featuredArticle.id) return null;
                        return (
                          <motion.div key={article.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: idx * 0.08 }}>
                          <Link
                            to={`/news/${article.slug}`}
                            className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400 cursor-pointer block"
                          >
                            <div className="relative h-52 overflow-hidden">
                              <img
                                src={article.image}
                                alt={article.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                loading="lazy"
                              />
                              <div className="absolute top-3 left-3">
                                <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-accent-600 rounded-full">{article.categoryName}</span>
                              </div>
                            </div>
                            <div className="p-5">
                              <div className="flex items-center gap-2 mb-3">
                                <time className="text-xs text-foreground-400">{new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
                                <span className="text-foreground-300">·</span>
                                <span className="text-xs text-foreground-400">{article.readTime}</span>
                              </div>
                              <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">{article.title}</h3>
                              <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-4">{article.excerpt}</p>
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] text-foreground-400">{article.author}</span>
                                <span className="text-xs font-semibold text-accent-600 group-hover:text-accent-500 transition-colors flex items-center gap-1 whitespace-nowrap">
                                  Read More <i className="ri-arrow-right-s-line"></i>
                                </span>
                              </div>
                            </div>
                          </Link>
                          </motion.div>
                        );
                      })}
                    </div>

                    {/* Load More */}
                    {hasMore && (
                      <div className="flex justify-center mt-10">
                        <button
                          onClick={() => setVisibleCount((prev) => prev + 6)}
                          className="px-8 py-3 bg-background-100 border border-background-200 text-foreground-700 font-semibold text-sm rounded-full hover:bg-background-200 hover:border-background-300 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
                        >
                          Load More Articles <i className="ri-arrow-down-s-line"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Newsletter CTA */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
              <i className="ri-mail-send-line text-xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">Stay Ahead of the Curve</h2>
            <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
              Subscribe to receive the latest trade insights, market analysis, and company updates directly in your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-5 py-3 text-sm bg-background-50 border border-background-200 rounded-full focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}